// Rich instructions editor (spec 01 §4 S4: "instructions rich editor with image
// upload"; §2.11 instructions jsonb shown on the floor PWA). A lightweight
// ordered-block editor: text blocks + image-reference blocks, reorderable.
//
// Binary image UPLOAD shares Module 02's presign/PDF media utility, which is not
// yet wired into this client (§12.4 / §13.7) — so image blocks reference an S3
// key (with alt text) and the UI states that upload lands with the shared media
// kit. The block document round-trips losslessly (unknown jsonb keys preserved).

import { useTranslation } from "react-i18next";
import { Button } from "../../../../../components/button";
import { Field } from "../../../../../components/field";
import { moveItem, type InstructionBlock, type InstructionsDoc } from "./model";

export function InstructionsEditor({
  doc,
  onChange,
  readOnly,
}: {
  doc: InstructionsDoc;
  onChange: (next: InstructionsDoc) => void;
  readOnly: boolean;
}) {
  const { t } = useTranslation();

  function setBlocks(blocks: InstructionBlock[]): void {
    onChange({ blocks, extra: doc.extra });
  }
  function updateBlock(index: number, next: InstructionBlock): void {
    setBlocks(doc.blocks.map((b, i) => (i === index ? next : b)));
  }
  function removeBlock(index: number): void {
    setBlocks(doc.blocks.filter((_, i) => i !== index));
  }
  function move(index: number, dir: -1 | 1): void {
    setBlocks(moveItem(doc.blocks, index, index + dir));
  }

  return (
    <div className="rte-instructions" data-testid="instructions-editor">
      {doc.blocks.length === 0 ? (
        <p className="rte-muted">{t("catalogue.routing.instructions.empty")}</p>
      ) : (
        <ol className="rte-block-list">
          {doc.blocks.map((block, index) => (
            <li className="rte-block" key={index} data-block-type={block.type}>
              <div className="rte-block-body">
                {block.type === "text" ? (
                  <textarea
                    aria-label={t("catalogue.routing.instructions.textLabel", { n: index + 1 })}
                    className="rte-block-text"
                    rows={2}
                    value={block.text}
                    readOnly={readOnly}
                    onChange={(e) => {
                      updateBlock(index, { type: "text", text: e.target.value });
                    }}
                  />
                ) : (
                  <div className="rte-block-image">
                    <Field label={t("catalogue.routing.instructions.imageKey")}>
                      <input
                        type="text"
                        value={block.s3_key}
                        readOnly={readOnly}
                        placeholder={t("catalogue.routing.instructions.imageKeyPlaceholder")}
                        onChange={(e) => {
                          updateBlock(index, {
                            type: "image",
                            s3_key: e.target.value,
                            alt: block.alt,
                          });
                        }}
                      />
                    </Field>
                    <Field label={t("catalogue.routing.instructions.imageAlt")}>
                      <input
                        type="text"
                        value={block.alt}
                        readOnly={readOnly}
                        onChange={(e) => {
                          updateBlock(index, {
                            type: "image",
                            s3_key: block.s3_key,
                            alt: e.target.value,
                          });
                        }}
                      />
                    </Field>
                    <p className="rte-hint">
                      {t("catalogue.routing.instructions.uploadUnavailable")}
                    </p>
                  </div>
                )}
              </div>
              {!readOnly ? (
                <div className="rte-block-actions">
                  <Button
                    size="sm"
                    aria-label={t("catalogue.routing.instructions.moveUp")}
                    disabled={index === 0}
                    onClick={() => {
                      move(index, -1);
                    }}
                  >
                    ↑
                  </Button>
                  <Button
                    size="sm"
                    aria-label={t("catalogue.routing.instructions.moveDown")}
                    disabled={index === doc.blocks.length - 1}
                    onClick={() => {
                      move(index, 1);
                    }}
                  >
                    ↓
                  </Button>
                  <Button
                    size="sm"
                    aria-label={t("catalogue.routing.instructions.removeBlock")}
                    onClick={() => {
                      removeBlock(index);
                    }}
                  >
                    ✕
                  </Button>
                </div>
              ) : null}
            </li>
          ))}
        </ol>
      )}

      {!readOnly ? (
        <div className="rte-block-add">
          <Button
            onClick={() => {
              setBlocks([...doc.blocks, { type: "text", text: "" }]);
            }}
          >
            {t("catalogue.routing.instructions.addText")}
          </Button>
          <Button
            onClick={() => {
              setBlocks([...doc.blocks, { type: "image", s3_key: "", alt: "" }]);
            }}
          >
            {t("catalogue.routing.instructions.addImage")}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
